import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface WelfareProposal {
  id: string;
  title: string;
  description: string;
  requestedAmount: number;
  currentAmount: number;
  status: 'active' | 'completed' | 'cancelled';
  createdBy: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
  deadline?: string;
  contributors: Array<{
    id: string;
    name: string;
    amount: number;
    contributedAt: string;
  }>;
}

const ChamaWelfare: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [proposals, setProposals] = useState<WelfareProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (chamaId) {
      loadWelfareProposals();
    }
  }, [chamaId]);

  const loadWelfareProposals = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaWelfare: No auth token found');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/welfare`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setProposals(data.data);
        } else {
          setProposals([]);
        }
      } else {
        console.error('❌ ChamaWelfare: Failed to load welfare proposals');
        setProposals([]);
      }
    } catch (error) {
      console.error('❌ ChamaWelfare: Error loading welfare proposals:', error);
      setProposals([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.primary;
      case 'completed': return colors.success;
      case 'cancelled': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '❤️';
      case 'completed': return '🎉';
      case 'cancelled': return '❌';
      default: return '❓';
    }
  };

  const filteredProposals = proposals.filter(proposal => {
    if (activeTab === 'all') return true;
    return proposal.status === activeTab;
  });

  const renderProposalCard = (proposal: WelfareProposal) => {
    const progressPercentage = (proposal.currentAmount / proposal.requestedAmount) * 100;
    const isCompleted = proposal.status === 'completed';
    const isActive = proposal.status === 'active';

    return (
      <div key={proposal.id} style={{ ...styles.proposalCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div style={styles.proposalHeader}>
          <div style={styles.proposalCreator}>
            <div style={styles.creatorAvatar}>
              {proposal.createdBy.avatar ? (
                <img src={proposal.createdBy.avatar} alt={proposal.createdBy.name} style={styles.avatarImage} />
              ) : (
                <span style={{ fontSize: '16px' }}>
                  {proposal.createdBy.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              )}
            </div>
            <div style={styles.creatorInfo}>
              <div style={{ ...styles.creatorName, color: colors.text }}>{proposal.createdBy.name}</div>
              <div style={{ ...styles.proposalDate, color: colors.textSecondary }}>
                {new Date(proposal.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div style={{ ...styles.proposalStatus, backgroundColor: getStatusColor(proposal.status) + '20', border: `1px solid ${getStatusColor(proposal.status)}` }}>
            <span style={{ fontSize: '16px', marginRight: '4px' }}>{getStatusIcon(proposal.status)}</span>
            <span style={{ ...styles.statusText, color: getStatusColor(proposal.status) }}>
              {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
            </span>
          </div>
        </div>

        <div style={styles.proposalContent}>
          <h3 style={{ ...styles.proposalTitle, color: colors.text }}>{proposal.title}</h3>
          <p style={{ ...styles.proposalDescription, color: colors.textSecondary }}>{proposal.description}</p>
        </div>

        <div style={styles.proposalProgress}>
          <div style={styles.progressHeader}>
            <div style={{ ...styles.progressAmount, color: colors.text }}>
              {formatCurrency(proposal.currentAmount)} / {formatCurrency(proposal.requestedAmount)}
            </div>
            <div style={{ ...styles.progressPercentage, color: colors.textSecondary }}>
              {progressPercentage.toFixed(1)}%
            </div>
          </div>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${Math.min(progressPercentage, 100)}%`,
                backgroundColor: isCompleted ? colors.success : colors.primary
              }}
            />
          </div>
        </div>

        {proposal.deadline && (
          <div style={styles.deadlineInfo}>
            <div style={{ ...styles.deadlineLabel, color: colors.textSecondary }}>Deadline</div>
            <div style={{ ...styles.deadlineDate, color: colors.text }}>
              {new Date(proposal.deadline).toLocaleDateString()}
            </div>
          </div>
        )}

        <div style={styles.proposalStats}>
          <div style={styles.statItem}>
            <div style={{ ...styles.statValue, color: colors.text }}>{proposal.contributors.length}</div>
            <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Contributors</div>
          </div>
          <div style={styles.statItem}>
            <div style={{ ...styles.statValue, color: colors.text }}>
              {formatCurrency(proposal.requestedAmount - proposal.currentAmount)}
            </div>
            <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Remaining</div>
          </div>
        </div>

        <div style={styles.proposalActions}>
          <button style={{ ...styles.actionButton, backgroundColor: colors.primary }}>
            View Details
          </button>
          {isActive && (
            <button style={{ ...styles.actionButton, backgroundColor: colors.success }}>
              Contribute
            </button>
          )}
        </div>

        {/* Recent Contributors */}
        {proposal.contributors.length > 0 && (
          <div style={styles.contributorsSection}>
            <div style={{ ...styles.contributorsTitle, color: colors.textSecondary }}>Recent Contributors</div>
            <div style={styles.contributorsList}>
              {proposal.contributors.slice(0, 3).map((contributor, index) => (
                <div key={contributor.id} style={styles.contributorItem}>
                  <div style={styles.contributorName}>{contributor.name}</div>
                  <div style={styles.contributorAmount}>{formatCurrency(contributor.amount)}</div>
                </div>
              ))}
              {proposal.contributors.length > 3 && (
                <div style={{ ...styles.moreContributors, color: colors.textSecondary }}>
                  +{proposal.contributors.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading welfare proposals...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Welfare Fund"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>❤️</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {proposals.filter(p => p.status === 'active').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Active Proposals</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>💰</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {formatCurrency(proposals.reduce((sum, p) => sum + p.currentAmount, 0))}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Contributed</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>🎯</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {proposals.filter(p => p.status === 'completed').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Completed</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>👥</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {proposals.reduce((sum, p) => sum + p.contributors.length, 0)}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Contributors</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {[
              { id: 'all', label: 'All Proposals', count: proposals.length },
              { id: 'active', label: 'Active', count: proposals.filter(p => p.status === 'active').length },
              { id: 'completed', label: 'Completed', count: proposals.filter(p => p.status === 'completed').length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...styles.tabButton,
                  backgroundColor: activeTab === tab.id ? colors.primary : 'transparent',
                  color: activeTab === tab.id ? 'white' : colors.text,
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Proposals List */}
          <div style={styles.proposalsList}>
            {filteredProposals.length > 0 ? (
              filteredProposals.map(renderProposalCard)
            ) : (
              <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <div style={styles.emptyIcon}>❤️</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No welfare proposals found</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  {activeTab === 'all'
                    ? 'No welfare proposals have been created yet.'
                    : `No ${activeTab} welfare proposals found.`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Create Proposal Button */}
          <button style={{ ...styles.createButton, backgroundColor: colors.primary }}>
            Create Welfare Proposal
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  proposalsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  proposalCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  proposalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  proposalCreator: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  creatorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    objectFit: 'cover',
  },
  creatorInfo: {
    flex: 1,
  },
  creatorName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  proposalDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  proposalStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  proposalContent: {
    marginBottom: spacing.md,
  },
  proposalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.sm,
  },
  proposalDescription: {
    fontSize: typography.fontSize.base,
    lineHeight: 1.5,
    margin: 0,
  },
  proposalProgress: {
    marginBottom: spacing.md,
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  progressAmount: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  progressPercentage: {
    fontSize: typography.fontSize.sm,
  },
  progressBar: {
    height: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  deadlineInfo: {
    marginBottom: spacing.md,
  },
  deadlineLabel: {
    fontSize: typography.fontSize.sm,
  },
  deadlineDate: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  proposalStats: {
    display: 'flex',
    gap: spacing.lg,
    marginBottom: spacing.md,
  },
  statItem: {
    flex: 1,
  },
  statValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  proposalActions: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  actionButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  contributorsSection: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: spacing.md,
  },
  contributorsTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  contributorsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  contributorItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contributorName: {
    fontSize: typography.fontSize.sm,
  },
  contributorAmount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  moreContributors: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    padding: spacing.xs,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  createButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    marginTop: spacing.lg,
  },
};

export default ChamaWelfare;